import { Cookie } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAnalytics } from '../lib/analytics';
import { useConsent } from '../lib/consent';
import { useT } from '../lib/i18n';
import { Button, Dialog, Switch } from './ui';

function ConsentSettings() {
  const t = useT();
  const { consent, settingsOpen, openSettings, decide } = useConsent();
  const [analytics, setAnalytics] = useState(consent?.analytics ?? false);
  return (
    <Dialog
      open={settingsOpen}
      onOpenChange={(o) => {
        openSettings(o);
        if (o) setAnalytics(useConsent.getState().consent?.analytics ?? false);
      }}
      title={t('consent.settingsTitle')}
      description={t('consent.settingsText')}
      footer={
        <>
          <Button onClick={() => decide({ analytics: false })}>{t('consent.essentialOnly')}</Button>
          <Button
            variant="primary"
            onClick={() => decide({ analytics })}
            data-testid="consent-save"
          >
            {t('consent.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex items-start gap-3 rounded-xl border border-zinc-200 p-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-zinc-900">{t('consent.essential')}</div>
            <p className="mt-0.5 text-xs text-zinc-500">{t('consent.essentialText')}</p>
          </div>
          <span className="shrink-0 text-xs font-medium text-zinc-400">{t('consent.always')}</span>
        </div>
        <div className="flex items-start gap-3 rounded-xl border border-zinc-200 p-4">
          <div className="flex-1">
            <div className="text-sm font-semibold text-zinc-900">{t('consent.analytics')}</div>
            <p className="mt-0.5 text-xs text-zinc-500">{t('consent.analyticsText')}</p>
          </div>
          <Switch checked={analytics} onChange={setAnalytics} />
        </div>
        <p className="text-xs text-zinc-500">
          {t('consent.more')}{' '}
          <Link to="/privacy" className="font-medium text-brand-600 hover:underline">
            {t('legal.privacy')}
          </Link>
        </p>
      </div>
    </Dialog>
  );
}

/**
 * Cookie banner: shown until the visitor chooses. "Accept" and "Essential only" are equally
 * prominent, as EU regulators expect; nothing optional runs before a choice.
 */
export function CookieConsent() {
  const t = useT();
  useAnalytics();
  const { consent, decide, openSettings, settingsOpen } = useConsent();
  return (
    <>
      {!consent && !settingsOpen && (
        <div
          className="fixed inset-x-3 bottom-3 z-[60] mx-auto max-w-3xl rounded-2xl border border-zinc-200 bg-white/95 p-4 shadow-2xl backdrop-blur sm:inset-x-6 sm:bottom-6 sm:p-5"
          role="dialog"
          aria-live="polite"
          aria-label={t('consent.title')}
          data-testid="cookie-banner"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <div className="flex flex-1 gap-3">
              <Cookie className="mt-0.5 size-5 shrink-0 text-brand-600" />
              <div className="text-sm text-zinc-600">
                <div className="font-semibold text-zinc-900">{t('consent.title')}</div>
                <p className="mt-0.5">
                  {t('consent.text')}{' '}
                  <Link to="/privacy" className="font-medium text-brand-600 hover:underline">
                    {t('legal.privacy')}
                  </Link>
                </p>
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={() => openSettings()}>
                {t('consent.customize')}
              </Button>
              <Button
                size="sm"
                onClick={() => decide({ analytics: false })}
                data-testid="consent-reject"
              >
                {t('consent.essentialOnly')}
              </Button>
              <Button
                size="sm"
                variant="primary"
                onClick={() => decide({ analytics: true })}
                data-testid="consent-accept"
              >
                {t('consent.accept')}
              </Button>
            </div>
          </div>
        </div>
      )}
      <ConsentSettings />
    </>
  );
}

/** "Cookie settings" link for footers and account settings. */
export function CookieSettingsLink({ className }: { className?: string }) {
  const t = useT();
  return (
    <button
      type="button"
      className={className}
      onClick={() => useConsent.getState().openSettings()}
    >
      {t('consent.link')}
    </button>
  );
}
